import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  NODE_EXECUTION_STATUS,
  SCENARIO_SCOPES,
} from '../../../common/constants/ScenarioConstants';
import SUCCESS_MESSAGES from '../../../common/constants/SuccessMessagesConstants';
import { request } from '../../../common/util/RestClient';
import envConfig from '../../../config';
import {
  activateScenario,
  archiveScenario,
  createScenario,
  deactivateScenario,
  deleteScenario,
  dismissNodeExecutionUnattendedList,
  fetchAllScenarios,
  fetchNodeExecutionAttendedFailList,
  fetchNodeExecutionAttendedSkipList,
  fetchNodeExecutionRejectedList,
  fetchNodeExecutionSummary,
  fetchScenarioById,
  openNodeExecutionAttendedListDrawer,
  openNodeExecutionRejectedListDrawer,
  updateScenario,
  duplicateScenario,
  openNodeExecutionCompletedListDrawer,
  fetchNodeExecutionCompletedList,
  retryFailedNodeExecutions,
  fetchScenarioEmailServiceType,
  renameScenario,
} from '../../routines/scenarios/scenarioRoutines';
import { GTM_TRACK_EVENT } from '../../../common/GoogleTagManager/GoogleTagManager';
import { GTM_EVENTS } from '../../../common/GoogleTagManager/GTMEventConstants';

const SCENARIO_MGT_URL = `${envConfig.APIG_URL}scenario-management`;

export function* fetchAllScenariosSaga() {
  try {
    const response = yield call(request, `${SCENARIO_MGT_URL}/scenarios`);
    yield put(fetchAllScenarios.success(response));
  } catch (error) {
    yield put(fetchAllScenarios.failure({ error }));
  } finally {
    yield put(fetchAllScenarios.fulfill());
  }
}

export function* fetchScenarioByIdSaga({ payload }) {
  try {
    const response = yield call(
      request,
      `${SCENARIO_MGT_URL}/scenarios/${payload.scenarioId}`
    );
    yield put(fetchScenarioById.success(response));
  } catch (error) {
    yield put(fetchScenarioById.failure({ error, noAlert: true }));
  } finally {
    yield put(fetchScenarioById.fulfill());
  }
}

export function* duplicateScenarioSaga({ payload }) {
  try {
    yield put(fetchScenarioById.trigger(payload));
    yield put(duplicateScenario.success(payload));
  } catch (error) {
    yield put(duplicateScenario.failure({ error }));
  } finally {
    yield put(duplicateScenario.fulfill());
  }
}

export function* createScenarioSaga({ payload }) {
  try {
    const { formData, callbackFn } = payload;
    const response = yield call(request, `${SCENARIO_MGT_URL}/scenarios`, {
      method: 'POST',
      payload: formData,
    });
    GTM_TRACK_EVENT(GTM_EVENTS.ACTIONS_SCENARIOS_CREATED);
    yield put(
      createScenario.success({
        response,
        successMessage: SUCCESS_MESSAGES.CREATE_SCENARIO,
      })
    );
    if (callbackFn && response?.data?.scenarioId) {
      callbackFn(response.data.scenarioId);
    }
  } catch (error) {
    yield put(createScenario.failure({ error }));
  } finally {
    yield put(createScenario.fulfill());
  }
}

export function* updateScenarioSaga({ payload }) {
  try {
    const { formData, scenarioId } = payload;
    GTM_TRACK_EVENT(GTM_EVENTS.ACTIONS_SCENARIOS_UPDATED);
    const response = yield call(
      request,
      `${SCENARIO_MGT_URL}/scenarios/${scenarioId}`,
      {
        method: 'PUT',
        payload: formData,
      }
    );
    yield put(
      updateScenario.success({
        response,
        successMessage: SUCCESS_MESSAGES.UPDATE_SCENARIO,
      })
    );
    yield put(fetchAllScenarios.trigger());
  } catch (error) {
    yield put(updateScenario.failure({ error }));
  } finally {
    yield put(updateScenario.fulfill());
  }
}

export function* archiveScenarioSaga({ payload }) {
  try {
    const { scenarioId, scope = SCENARIO_SCOPES.LIST_VIEW } = payload;
    const response = yield call(
      request,
      `${SCENARIO_MGT_URL}/scenarios/${scenarioId}/archive`,
      {
        method: 'DELETE',
      }
    );
    yield put(
      archiveScenario.success({
        response,
        successMessage: SUCCESS_MESSAGES.ARCHIVE_SCENARIO,
      })
    );
    if (scope === SCENARIO_SCOPES.VIEW_CANVAS) {
      yield put(fetchScenarioById.trigger({ scenarioId }));
    } else if (scope === SCENARIO_SCOPES.LIST_VIEW) {
      yield put(fetchAllScenarios.trigger());
    }
  } catch (error) {
    yield put(archiveScenario.failure({ error }));
  } finally {
    yield put(archiveScenario.fulfill());
  }
}
export async function validateScenarioExists({ name, level }) {
  const URL = `${SCENARIO_MGT_URL}/scenarios/search?level=${level}&name=${name}`;

  try {
    return await request(URL);
  } catch (error) {
    return null;
  }
}

export function* deleteScenarioSaga({ payload }) {
  try {
    const {
      scenarioId,
      scope = SCENARIO_SCOPES.LIST_VIEW,
      callbackFn,
    } = payload;
    GTM_TRACK_EVENT(GTM_EVENTS.ACTIONS_SCENARIOS_DELETED);
    const response = yield call(
      request,
      `${SCENARIO_MGT_URL}/scenarios/${scenarioId}/delete`,
      {
        method: 'DELETE',
      }
    );
    yield put(
      deleteScenario.success({
        response,
        successMessage: SUCCESS_MESSAGES.DELETE_SCENARIO,
      })
    );

    if (callbackFn) callbackFn();
    if (scope === SCENARIO_SCOPES.LIST_VIEW) {
      yield put(fetchAllScenarios.trigger());
    }
  } catch (error) {
    yield put(deleteScenario.failure({ error }));
  } finally {
    yield put(deleteScenario.fulfill());
  }
}

export function* activateScenarioSaga({ payload }) {
  try {
    const { scenarioId, scope = SCENARIO_SCOPES.LIST_VIEW } = payload;
    const response = yield call(
      request,
      `${SCENARIO_MGT_URL}/scenarios/${scenarioId}/activate`,
      { method: 'PATCH', payload: {} }
    );
    yield put(
      activateScenario.success({
        response,
        successMessage: SUCCESS_MESSAGES.ACTIVATE_SCENARIO,
      })
    );
    if (scope === SCENARIO_SCOPES.VIEW_CANVAS) {
      yield put(fetchScenarioById.trigger({ scenarioId }));
    } else if (scope === SCENARIO_SCOPES.LIST_VIEW) {
      yield put(fetchAllScenarios.trigger());
    }
  } catch (error) {
    yield put(activateScenario.failure({ error }));
  } finally {
    yield put(activateScenario.fulfill());
  }
}

export function* deactivateScenarioSaga({ payload }) {
  try {
    const { scenarioId, scope = SCENARIO_SCOPES.LIST_VIEW } = payload;
    const response = yield call(
      request,
      `${SCENARIO_MGT_URL}/scenarios/${scenarioId}/deactivate`,
      { method: 'PATCH', payload: {} }
    );
    yield put(
      deactivateScenario.success({
        response,
        successMessage: SUCCESS_MESSAGES.DEACTIVATE_SCENARIO,
      })
    );
    if (scope === SCENARIO_SCOPES.VIEW_CANVAS) {
      yield put(fetchScenarioById.trigger({ scenarioId }));
    } else if (scope === SCENARIO_SCOPES.LIST_VIEW) {
      yield put(fetchAllScenarios.trigger());
    }
  } catch (error) {
    yield put(deactivateScenario.failure({ error }));
  } finally {
    yield put(deactivateScenario.fulfill());
  }
}

export function* fetchScenarioExecutionSummarySaga({ payload }) {
  const { status, nodeId } = payload;
  try {
    const response = yield call(
      request,
      `${SCENARIO_MGT_URL}/nodes/${nodeId}/node-execution-runs?status=${status}`
    );
    yield put(fetchNodeExecutionSummary.success(response));
  } catch (error) {
    yield put(fetchNodeExecutionSummary.failure({ error }));
  } finally {
    yield put(fetchNodeExecutionSummary.fulfill());
  }
}

function* fetchNodeExecutionAttendedFailSaga(nodeId) {
  try {
    yield put(fetchNodeExecutionAttendedFailList.trigger());
    const response = yield call(
      request,
      `${SCENARIO_MGT_URL}/nodes/${nodeId}/node-execution-runs?status=${NODE_EXECUTION_STATUS.ATTENDED_FAILURE}`
    );
    yield put(fetchNodeExecutionAttendedFailList.success(response));
  } catch (error) {
    yield put(fetchNodeExecutionAttendedFailList.failure({ error }));
  } finally {
    yield put(fetchNodeExecutionAttendedFailList.fulfill());
  }
}

function* fetchNodeExecutionAttendedSkipSaga(nodeId) {
  try {
    yield put(fetchNodeExecutionAttendedSkipList.trigger());
    const response = yield call(
      request,
      `${SCENARIO_MGT_URL}/nodes/${nodeId}/node-execution-runs?status=${NODE_EXECUTION_STATUS.ATTENDED_SKIP}`
    );
    yield put(fetchNodeExecutionAttendedSkipList.success(response));
  } catch (error) {
    yield put(fetchNodeExecutionAttendedSkipList.failure({ error }));
  } finally {
    yield put(fetchNodeExecutionAttendedSkipList.fulfill());
  }
}

export function* fetchNodeExecutionAttendedLists({ payload }) {
  const { nodeId } = payload;
  yield all([
    call(fetchNodeExecutionAttendedSkipSaga, nodeId),
    call(fetchNodeExecutionAttendedFailSaga, nodeId),
  ]);
}

function* fetchNodeExecutionRejectedSaga({ payload }) {
  const { nodeId } = payload;
  try {
    yield put(fetchNodeExecutionRejectedList.trigger());
    const response = yield call(
      request,
      `${SCENARIO_MGT_URL}/nodes/${nodeId}/node-execution-runs?status=${NODE_EXECUTION_STATUS.REJECTED}`
    );
    yield put(fetchNodeExecutionRejectedList.success(response));
  } catch (error) {
    yield put(fetchNodeExecutionRejectedList.failure({ error }));
  } finally {
    yield put(fetchNodeExecutionRejectedList.fulfill());
  }
}

function* fetchNodeExecutionCompletedSaga({ payload }) {
  const { nodeId } = payload;
  try {
    yield put(fetchNodeExecutionCompletedList.trigger());
    const response = yield call(
      request,
      `${SCENARIO_MGT_URL}/nodes/${nodeId}/node-execution-runs?status=${NODE_EXECUTION_STATUS.COMPLETED}`
    );
    yield put(fetchNodeExecutionCompletedList.success(response));
  } catch (error) {
    yield put(fetchNodeExecutionCompletedList.failure({ error }));
  } finally {
    yield put(fetchNodeExecutionCompletedList.fulfill());
  }
}

export function* dismissNodeExecutionUnattended({ payload }) {
  try {
    const { executionRunIds, nodeId, status } = payload;
    const response = yield call(
      request,
      `${SCENARIO_MGT_URL}/nodes/node-execution-runs/dismiss`,
      {
        method: 'POST',
        payload: {
          ids: executionRunIds,
        },
      }
    );
    yield put(
      dismissNodeExecutionUnattendedList.success({
        nodeId,
        status,
        dismissedLength: response.data.nodeExecutionRunIds.length,
      })
    );
  } catch (error) {
    yield put(dismissNodeExecutionUnattendedList.failure({ error }));
  } finally {
    yield put(dismissNodeExecutionUnattendedList.fulfill());
  }
}

export function* retryFailedNodeExecutionsSaga({ payload }) {
  try {
    const { ids } = payload;
    const response = yield call(
      request,
      `${SCENARIO_MGT_URL}/nodes/node-execution-runs/retry`,
      {
        method: 'POST',
        payload: {
          ids,
        },
      }
    );
    yield put(retryFailedNodeExecutions.success(response));
  } catch (error) {
    yield put(retryFailedNodeExecutions.failure({ error }));
  } finally {
    yield put(retryFailedNodeExecutions.fulfill());
  }
}

export function* fetchScenarioEmailServiceTypeSaga() {
  try {
    const response = yield call(
      request,
      `${SCENARIO_MGT_URL}/scenarios/config?configKey=EMAIL_SERVICE_TYPE`
    );
    yield put(fetchScenarioEmailServiceType.success(response));
  } catch (error) {
    yield put(fetchScenarioEmailServiceType.failure({ error }));
  } finally {
    yield put(fetchScenarioEmailServiceType.fulfill());
  }
}

export function* renameScenarioSaga({ payload: data }) {
  try {
    const {
      scenarioId,
      name,
      description,
      scope = SCENARIO_SCOPES.LIST_VIEW,
    } = data;
    const payload = { name, description: !description ? null : description };
    const response = yield call(
      request,
      `${SCENARIO_MGT_URL}/scenarios/${scenarioId}/rename`,
      {
        method: 'PATCH',
        payload,
      }
    );
    yield put(
      renameScenario.success({
        response,
        scope,
        successMessage: SUCCESS_MESSAGES.UPDATE_SCENARIO,
      })
    );
    if (scope === SCENARIO_SCOPES.VIEW_CANVAS) {
      yield put(fetchScenarioById.trigger({ scenarioId }));
    } else if (scope === SCENARIO_SCOPES.LIST_VIEW) {
      yield put(fetchAllScenarios.trigger());
    }
  } catch (error) {
    yield put(renameScenario.failure({ error }));
  } finally {
    yield put(renameScenario.fulfill());
  }
}

export default function* root() {
  yield all([
    takeLatest(fetchAllScenarios.TRIGGER, fetchAllScenariosSaga),
    takeLatest(fetchScenarioById.TRIGGER, fetchScenarioByIdSaga),
    takeLatest(duplicateScenario.TRIGGER, duplicateScenarioSaga),
    takeEvery(createScenario.TRIGGER, createScenarioSaga),
    takeEvery(archiveScenario.TRIGGER, archiveScenarioSaga),
    takeEvery(updateScenario.TRIGGER, updateScenarioSaga),
    takeEvery(deleteScenario.TRIGGER, deleteScenarioSaga),
    takeEvery(activateScenario.TRIGGER, activateScenarioSaga),
    takeEvery(deactivateScenario.TRIGGER, deactivateScenarioSaga),
    takeEvery(renameScenario.TRIGGER, renameScenarioSaga),
    takeLatest(
      fetchNodeExecutionSummary.TRIGGER,
      fetchScenarioExecutionSummarySaga
    ),
    takeLatest(
      openNodeExecutionAttendedListDrawer.TRIGGER,
      fetchNodeExecutionAttendedLists
    ),
    takeLatest(
      dismissNodeExecutionUnattendedList.TRIGGER,
      dismissNodeExecutionUnattended
    ),
    takeLatest(
      openNodeExecutionRejectedListDrawer.TRIGGER,
      fetchNodeExecutionRejectedSaga
    ),
    takeLatest(
      openNodeExecutionCompletedListDrawer.TRIGGER,
      fetchNodeExecutionCompletedSaga
    ),
    takeLatest(
      retryFailedNodeExecutions.TRIGGER,
      retryFailedNodeExecutionsSaga
    ),
    takeLatest(
      fetchScenarioEmailServiceType.TRIGGER,
      fetchScenarioEmailServiceTypeSaga
    ),
  ]);
}
